import { useTranslations } from 'next-intl';
import {
  CheckoutStateEnum,
  ICheckoutCart,
  PaymentMethodsEnum,
  PaymentMethodTypes,
  PSPEnum,
} from '@monofront/types';

import { useMediaQuery } from '@monofront/business/hooks';
import {
  ABTEST_APPLE_PAY,
  ABTEST_CHECKOUT_MONTHLY_MEMBERSHIP,
  ABTEST_CHECKOUT_PAYMENT_TEXT_POSITION,
  PaypalTypesEnum,
} from '@monofront/business/constants';
import RadioLine from '@monofront/design-system/components/Form/RadioLine';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';

import { useAuthentication } from '@context/businessContainer';
import logger from '@utils/logger';
import OptionCheckoutComCard from './OptionCheckoutComCard';
import { useCheckoutPayment } from '../contexts';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import { useMemo } from 'react';
import { usePaypalContext } from '@context/PaypalProvider';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import CharacterSad from '@monofront/design-system/visuals/Illustrations/CharacterSad';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import useApplePayEligibility from '../hooks/useApplePayEligibility';
interface IPaymentMethodsListNewProps {
  checkoutCart: ICheckoutCart;
}

const PaymentMethodsListNew: React.FC<IPaymentMethodsListNewProps> = ({
  checkoutCart,
}) => {
  const tCheckout = useTranslations('Checkout');
  const tCart = useTranslations('Cart');
  const tCheckoutPayment = useTranslations('Checkout.Payment');
  const { authUser } = useAuthentication();
  const isMobile = useMediaQuery(800);
  const { isReady: isPaypalReady, isMethodEligible } = usePaypalContext();
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);
  const {
    isOrderValidationInProgress,
    selectedPaymentRadioId,
    newPaymentOptions,
    savedPaymentMethods,
    selectNewCheckoutComOption,
    selectNewPaypalOption,
    selectNewApplePayOption,
  } = useCheckoutPayment();
  const isCheckoutMonthlyMembershipABTestEnabled = useFeatureIsOn(
    ABTEST_CHECKOUT_MONTHLY_MEMBERSHIP,
  );
  const isPaymentTextPositionABTestEnabled = useFeatureIsOn(
    ABTEST_CHECKOUT_PAYMENT_TEXT_POSITION,
  );
  const isApplePayABTestEnabled = useFeatureIsOn(ABTEST_APPLE_PAY);
  const { isApplePayAvailable } = useApplePayEligibility();

  const usablePaymentOptions = useMemo(() => {
    return newPaymentOptions.filter((paymentOption) => {
      if (!paymentOption.publishableKey) {
        const warnMessage = {
          message:
            '[checkout] Cannot display a user Payment Option without publishableKey.',
          context: {
            user_id: authUser?.uid,
            cart_id: checkoutCart?.id,
            payment_option_id: paymentOption.id,
            payment_option_code: paymentOption.code,
          },
        };

        logger.warn(warnMessage.message, warnMessage.context);

        return false;
      }

      return true;
    });
  }, [newPaymentOptions.length]);

  const checkoutComCardOption = usablePaymentOptions.find(
    ({ method }) => PaymentMethodsEnum.CARD === method,
  );
  const paypalOption = usablePaymentOptions.find(
    ({ psp }) => PSPEnum.PAYPAL === psp,
  );
  const applePayOption =
    isApplePayABTestEnabled &&
    isApplePayAvailable &&
    usablePaymentOptions.find(
      ({ method }) => PaymentMethodsEnum.APPLE_PAY === method,
    );

  // note: the same Sylius payment option is used for both Paypal and Paypal4x
  const paypal4xOption =
    isPaypalReady && isMethodEligible(checkoutCart.total / 100)
      ? usablePaymentOptions.find(({ psp }) => PSPEnum.PAYPAL === psp)
      : undefined;

  if (!usablePaymentOptions.length) {
    return CheckoutStateEnum.PAYMENT_SKIPPED ===
      checkoutCart.checkoutState ? null : (
      <InformativeBox
        backgroundColor={'var(--clr-secondary-3)'}
        hasImageOnMobile
        icon={<CharacterSad width={34} height={34} aria-hidden="true" />}
        smallBox={true}
        UNSAFE_className={'tw-mb-5'}
      >
        <p className="tw-lf-body-regular-sm tw-leading-normal">
          {tCheckoutPayment.rich('messageCannotFetchNewPM', {
            strong: (children) => <strong>{children}</strong>,
            br: () => <br />,
          })}
        </p>
      </InformativeBox>
    );
  }

  return (
    <>
      <section className="payment-methods-list-new [.payment-methods-list-existing+&]:tw-mt-6">
        {isSubscriptionAvailable &&
          isCheckoutMonthlyMembershipABTestEnabled && (
            <h3
              className={`tw-lf-body-bold-md tw-mb-4 desktop:tw-mb-6 ${
                isPaymentTextPositionABTestEnabled ? 'tw-mt-5' : ''
              }`}
            >
              {tCart('choosePaymentMethod')}
            </h3>
          )}
        {savedPaymentMethods.length > 0 && (
          <h2 className="tw-lf-body-xbold-md tw-mb-2">
            {tCheckout('useNewPaymentMethod')}
          </h2>
        )}
        <ul>
          {checkoutComCardOption && (
            <li>
              <RadioLine
                id={checkoutComCardOption.code}
                name="checkout-available-payment-list"
                value={checkoutComCardOption.code}
                checked={selectedPaymentRadioId === checkoutComCardOption.code}
                handleChecked={() => {
                  selectNewCheckoutComOption(
                    checkoutComCardOption.code,
                    checkoutComCardOption.method,
                  );
                }}
                required
                disabled={isOrderValidationInProgress}
                isTextBoldWhenChecked
                isVerticallyCentered
                dataTestId={`CheckoutPayment-RadioLine-new-card-payment-${checkoutComCardOption.code}`}
              >
                <div className="tw-flex tw-items-center tw-gap-3">
                  {tCheckout(
                    savedPaymentMethods.length > 0
                      ? 'addNewCreditCard'
                      : 'payWithCreditCard',
                  )}
                  {!isMobile && (
                    <ResponsiveImage
                      alt=""
                      src="/images/checkout/payment-method.svg"
                      height={20}
                      width={108}
                      aria-hidden
                    />
                  )}
                </div>
              </RadioLine>
              {selectedPaymentRadioId === checkoutComCardOption.code && (
                <OptionCheckoutComCard />
              )}
            </li>
          )}
          {paypalOption && (
            <li>
              <RadioLine
                id={paypalOption.code}
                name="checkout-available-payment-list"
                value={paypalOption.code}
                checked={selectedPaymentRadioId === paypalOption.code}
                handleChecked={() => {
                  if (isSubscriptionAvailable) {
                    selectNewPaypalOption(
                      paypalOption.code,
                      {
                        paypalType: PaypalTypesEnum.PAYPAL_AGREEMENT,
                        paymentMethodCode: paypalOption.code,
                      },
                      paypalOption.method,
                    );
                  } else {
                    selectNewPaypalOption(
                      paypalOption.code,
                      {
                        paypalType: PaypalTypesEnum.PAYPAL,
                        paymentMethodCode: paypalOption.code,
                      },
                      paypalOption.method,
                    );
                  }
                }}
                required
                disabled={isOrderValidationInProgress}
                isTextBoldWhenChecked
                isVerticallyCentered
                dataTestId={`CheckoutPayment-RadioLine-new-paypal-payment-${paypalOption.code}`}
              >
                <div className="tw-flex tw-items-center tw-gap-3">
                  {tCheckout('payWithPaypal')}
                  {!isMobile && (
                    <ResponsiveImage
                      alt=""
                      src="/images/checkout/paypal.svg"
                      height={20}
                      width={108}
                      aria-hidden
                    />
                  )}
                </div>
              </RadioLine>
              {selectedPaymentRadioId === paypalOption.code && (
                <p className="tw-my-4">{tCheckout('paypalInfos')}</p>
              )}
            </li>
          )}
          {paypal4xOption && (
            <li>
              <RadioLine
                id={`${paypal4xOption.code}-4x`}
                name="checkout-available-payment-list"
                value={`${paypal4xOption.code}-4x`}
                checked={selectedPaymentRadioId === `${paypal4xOption.code}-4x`}
                handleChecked={() => {
                  selectNewPaypalOption(
                    `${paypal4xOption.code}-4x`,
                    {
                      paypalType: PaypalTypesEnum.PAYPAL4X,
                      paymentMethodCode: paypal4xOption.code,
                    },
                    PaymentMethodTypes.PAYPAL4X,
                  );
                }}
                required
                disabled={isOrderValidationInProgress}
                isTextBoldWhenChecked
                isVerticallyCentered
                dataTestId={`CheckoutPayment-RadioLine-new-paypal-payment-${paypal4xOption.code}-4x`}
              >
                <div className="tw-flex tw-items-center tw-gap-3">
                  {tCheckout('payWithPaypal4x')}
                  {!isMobile && (
                    <ResponsiveImage
                      alt=""
                      src="/images/checkout/paypal.svg"
                      height={20}
                      width={108}
                      aria-hidden
                    />
                  )}
                </div>
              </RadioLine>
              {selectedPaymentRadioId === `${paypal4xOption.code}-4x` && (
                <p className="tw-my-4">{tCheckout('paypalInfos')}</p>
              )}
            </li>
          )}
          {applePayOption && (
            <li>
              <RadioLine
                id={applePayOption.code}
                name="checkout-available-payment-list"
                value={applePayOption.code}
                checked={selectedPaymentRadioId === applePayOption.code}
                handleChecked={() => {
                  selectNewApplePayOption(
                    applePayOption.code,
                    applePayOption.method,
                  );
                }}
                required
                disabled={isOrderValidationInProgress}
                isTextBoldWhenChecked
                isVerticallyCentered
                dataTestId={`CheckoutPayment-RadioLine-new-card-payment-${applePayOption.code}`}
              >
                <div className="tw-flex tw-items-center tw-gap-3">
                  <p>{tCheckout('payWithApplePay')}</p>
                  {!isMobile && (
                    <ResponsiveImage
                      alt=""
                      src="/images/checkout/apple-pay.svg"
                      height={24}
                      width={36}
                      aria-hidden
                    />
                  )}
                </div>
              </RadioLine>
            </li>
          )}
        </ul>
      </section>
    </>
  );
};

export default PaymentMethodsListNew;
